import React from 'react';
import { cloneDeep, get, isEmpty, isEqual, isUndefined, toLower } from 'lodash';
import moment from 'moment';
import {
  storageTypes,
  menuEntityTypesConfig,
  priceprecision,
  recommendedCategory,
} from '../constants/globals';
import { getItem, setItem } from '../utils/storage';
import { differenceBetween, checkTimeInRange } from '../utils/qwikyLocalizationHelper';
import orderingRoutes from '../routes/ordering';
import rewardsRoutes from '../routes/rewards';
import { isNativeWebView, roundOff } from '../utils/helpers';
import { nativeEventType, foodItemType, pointUnits } from '../config';
import Icons from '../components/Icons';

export const handleLinkUrl = ({ linkUrl, context }) => {
  if (isNativeWebView) {
    const data = {
      shareType: { type: 'link-url', url: linkUrl, context },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(linkUrl, '_blank');
  }
};

export const birthdayRewardAvailableDate = (dateOfBirth, months = 1, type = 'view') => {
  let startDate = isEqual(type, 'view')
    ? moment(dateOfBirth)
        .set('year', moment().get('year'))
        .subtract(months, 'months')
        .startOf('month')
    : moment(dateOfBirth).set('year', moment().get('year')).startOf('month');
  let endDate = moment(dateOfBirth).set('year', moment().get('year')).endOf('month');
  if (moment().isAfter(endDate)) {
    return { startDate: startDate.add(1, 'year'), endDate: endDate.add(1, 'year').endOf('month') };
  } else {
    return { startDate, endDate: endDate };
  }
};

export const anniversaryRewardAvailableDate = (dateOfanniversary, months = 1, type = 'view') => {
  let startDate = isEqual(type, 'view')
    ? moment(dateOfanniversary)
        .set('year', moment().get('year'))
        .subtract(months, 'months')
        .startOf('month')
    : moment(dateOfanniversary).set('year', moment().get('year')).startOf('month');
  let endDate = moment(dateOfanniversary).set('year', moment().get('year')).endOf('month');
  if (moment().isAfter(endDate)) {
    return { startDate: startDate.add(1, 'year'), endDate: endDate.add(1, 'year').endOf('month') };
  } else {
    return { startDate, endDate: endDate };
  }
};

export const checkIfBirthdayCouponNotSubscribed = (dateOfBirth, months = 1) => {
  let startDate = birthdayRewardAvailableDate(dateOfBirth, months).startDate;
  let endDate = birthdayRewardAvailableDate(dateOfBirth).endDate;
  if (moment().isAfter(startDate) && moment().isBefore(endDate) && !isEmpty(dateOfBirth)) {
    return true;
  }
};

export const checkIfAnniversaryCouponNotSubscribed = (dateOfanniversary, months = 1) => {
  let startDate = anniversaryRewardAvailableDate(dateOfanniversary, months).startDate;
  let endDate = anniversaryRewardAvailableDate(dateOfanniversary).endDate;
  if (moment().isAfter(startDate) && moment().isBefore(endDate) && !isEmpty(dateOfanniversary)) {
    return true;
  }
};

export const checkGuestFirstLogin = (rewardJoiningDate, brandGuestId) => {
  if (differenceBetween(moment(), rewardJoiningDate) > 0) {
    return false;
  } else if (
    isEqual(getItem(storageTypes.localStorage, 'isWelcomeMessageShown' + brandGuestId), true)
  ) {
    return false;
  } else {
    return true;
  }
};

export const checkIsGuestJoinedRewardPriorUpdate = ({ rewardJoiningDate, rewardUpdateDate }) => {
  const currentDate = moment().format();
  if (
    moment(rewardJoiningDate).isBefore(currentDate) &&
    moment(rewardJoiningDate).isBefore(rewardUpdateDate)
  ) {
    return true;
  } else {
    return false;
  }
};

export const renderList = (numberOfObjects) => {
  const list = [];
  for (let index = 0; index < numberOfObjects; index++) {
    list.push({});
  }
  return list;
};

export const handlePhoneNumber = ({ countryCode, phoneNumber }) => {
  if (window.ReactNativeWebView) {
    const data = {
      shareType: { type: 'mail', url: `tel:${phoneNumber}` },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(`tel:${countryCode}${phoneNumber}`, '_self');
  }
};

export const getMappingSteps = (length) => {
  switch (length) {
    case 1:
      return [100];
    case 2:
      return [50, 100];
    case 3:
      return [33.6, 67.8, 100];
    default:
      return [25, 25, 25, 25];
  }
};

export const getOrderingRouteContext = (selectedTable) => {
  let scanCode = get(selectedTable, 'scanCode', '');
  if (!isEmpty(scanCode)) {
    return `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.ordering}/${orderingRoutes.containers.menu}/${scanCode}`;
  } else {
    return `/${rewardsRoutes.rewards}/${rewardsRoutes.containers.ordering}`;
  }
};

export const extractDataFromNative = ({
  message,
  history,
  selectedTable,
  handleNativeBackCTA,
  handleLogout,
  handleNativeRouteName,
  handleClearViewCartItems,
  handleUpdateCart,
  handleRestaurantSelection,
  handleBrandGuestData,
  handleAuthTokenUpdated,
  handleAppResourcesUpdated,
}) => {
  let dataFromApp = get(message, 'data', '');
  if (!isEmpty(dataFromApp)) {
    dataFromApp = dataFromApp && JSON.parse(dataFromApp);
    const { eventType, data, payload = {} } = dataFromApp;
    switch (eventType) {
      case nativeEventType.nativeAppUpdateAvailable:
        setItem(storageTypes.sessionStorage, 'nativeAppUpdateAvailable', data);
        break;
      case nativeEventType.appVersion:
        data && setItem(storageTypes.sessionStorage, 'appVersion', data);
        break;
      case nativeEventType.location:
        const draftLocationCoordinates = !isEmpty(get(data, 'coords'))
          ? {
              lat: get(data, 'coords.latitude'),
              lng: get(data, 'coords.longitude'),
            }
          : {};

        const draftLocationPermissionStatus = get(data, 'locationPermissionStatus');

        if (!isEmpty(draftLocationCoordinates))
          setItem(storageTypes.localStorage, 'geoLocation', draftLocationCoordinates);
        if (!isEmpty(draftLocationPermissionStatus))
          setItem(
            storageTypes.localStorage,
            'geoLocationPermissionStatus',
            draftLocationPermissionStatus,
          );
        window.dispatchEvent(new Event('NewLocationSet'));
        // Dispatching this event to listen location change wherever needed
        break;
      case nativeEventType.currentLocation:
        const locationCoordinates = {
          lat: get(data, 'coords.latitude'),
          lng: get(data, 'coords.longitude'),
        };

        window.dispatchEvent(
          new CustomEvent('DraftCurrentLocationSet', { detail: { locationCoordinates } }),
        );
        break;
      case nativeEventType.routeName:
        if (isEqual(data, orderingRoutes.ordering)) {
          let updatedRoute = getOrderingRouteContext(selectedTable);
          history.push(updatedRoute);
        } else {
          history.push(`/${data}`, { payload });
        }
        break;
      case nativeEventType.hardwareBackPress:
        handleNativeBackCTA();
        break;
      case nativeEventType.appBecameActive:
        window.dispatchEvent(new Event(nativeEventType.appBecameActive));
        break;
      case 'pagesInNative':
        setItem(storageTypes.localStorage, 'pagesInNative', data);
        break;
      case nativeEventType.canGoBack:
        setItem(storageTypes.localStorage, nativeEventType.canGoBack, data);
        break;
      case nativeEventType.canExitModule:
        setItem(storageTypes.localStorage, nativeEventType.canExitModule, data);
        break;
      case 'logout':
        handleLogout(data);
        break;
      case 'scrollToTop':
        if (window.pageYOffset > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          setTimeout(() => {
            if (window.pageYOffset > 0) {
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case nativeEventType.nativeRouteName:
        handleNativeRouteName(data);
        break;
      case nativeEventType.updateMenuCart:
        handleUpdateCart(data);
        break;
      case nativeEventType.restaurantSelection:
        handleRestaurantSelection(data);
        break;
      case nativeEventType.brandGuestData:
        handleBrandGuestData(data);
        break;
      case 'auth-token-updated':
        handleAuthTokenUpdated(data);
        break;
      case nativeEventType.appResourcesUpdated:
        if (handleAppResourcesUpdated) handleAppResourcesUpdated(data);
        break;
      default:
        break;
    }
  }
};

export const checkVIPFlowApplicable = ({ applicableBrandModules, isBetaUser }) => {
  let isVIPPassFlowApplicable = '';
  const isVIPFlowActive = get(
    applicableBrandModules.find((bModule) => bModule.code === 'pass'),
    'isActive',
    false,
  );

  const isVIPFlowActiveForBetaUsers = get(
    applicableBrandModules.find((bModule) => bModule.code === 'pass'),
    'isBeta',
    false,
  );
  if (isVIPFlowActive) {
    if (isVIPFlowActiveForBetaUsers) {
      if (isBetaUser) {
        isVIPPassFlowApplicable = true;
      } else {
        isVIPPassFlowApplicable = false;
      }
    } else {
      isVIPPassFlowApplicable = true;
    }
  } else {
    isVIPPassFlowApplicable = false;
  }
  return isVIPPassFlowApplicable;
};

export const renderCurrenyName = (currencyName) => {
  return !isEmpty(currencyName) ? currencyName.toUpperCase().split(' ').join('\n') : '';
};

export const checkBrandModuleApplicable = ({ applicableBrandModules, moduleName }) => {
  let isModuleApplicable = false;
  const isModuleActive = get(
    applicableBrandModules.find((bModule) => bModule.code === moduleName),
    'isActive',
    false,
  );
  const isModuleActiveBeta = get(
    applicableBrandModules.find((bModule) => bModule.code === moduleName),
    'isBeta',
    false,
  );

  if (isModuleActive) {
    isModuleApplicable = true;
  } else if (isModuleActiveBeta) {
    isModuleApplicable = true;
  }

  return isModuleApplicable;
};

const filterMenuItemIdMatch = ({ filterItems, menu, categoryId, subCategoryId }) => {
  if (
    filterItems.some((item) => {
      if (isEqual(item.itemType, menuEntityTypesConfig.menuCategory)) {
        return isEqual(item.menuItemId, categoryId) || isEqual(item.menuItemId, subCategoryId);
      } else if (isEqual(item.itemType, menuEntityTypesConfig.menuItem)) {
        return isEqual(item.menuItemId, menu.ownerParentId);
      }
    })
  ) {
    return true;
  }
};

export const filterMenuItems = ({
  searchValue,
  foodType,
  menu,
  categoryMatch,
  subCategoryMatch,
  filterItems,
  categoryId,
  subCategoryId,
  excludedItems,
  maxItemPrice,
}) => {
  let foodTypeFilter = isEqual(foodType, foodItemType.nonVeg.name)
    ? [foodType, foodItemType.egg.name]
    : [foodType];
  let currentExcludedItems = [];
  const currentBogoAction =
    !isEmpty(filterItems) &&
    get(
      filterItems.find((item) => item),
      'action',
    );
  if (!isEmpty(currentBogoAction)) {
    currentExcludedItems = excludedItems.filter((item) => isEqual(item.action, currentBogoAction));
  }
  const priceUnit = !isEmpty(menu.defaultVariation)
    ? get(menu, 'defaultVariation.price', 0)
    : get(menu, 'price', 0);
  const priceMatch = maxItemPrice > 0 ? priceUnit <= maxItemPrice : true;
  const includedMatch = !isEmpty(filterItems)
    ? filterMenuItemIdMatch({ filterItems, menu, categoryId, subCategoryId })
    : true;
  const excludedMatch = !isEmpty(currentExcludedItems)
    ? filterMenuItemIdMatch({
        filterItems: currentExcludedItems,
        menu,
        categoryId,
        subCategoryId,
      })
    : false;

  const matchMultiplier = includedMatch && !excludedMatch && priceMatch;

  if (!isEmpty(searchValue) && !isEmpty(foodType) && !isEqual(foodType, foodItemType.all.name)) {
    if (toLower(menu.name).includes(searchValue) && foodTypeFilter.includes(menu.type)) {
      return matchMultiplier;
    } else if (
      !isEmpty(subCategoryMatch) &&
      toLower(subCategoryMatch).includes(searchValue) &&
      foodTypeFilter.includes(menu.type)
    ) {
      return matchMultiplier;
    } else if (
      !isEmpty(categoryMatch) &&
      toLower(categoryMatch).includes(searchValue) &&
      foodTypeFilter.includes(menu.type)
    ) {
      return matchMultiplier;
    }
  } else if (!isEmpty(searchValue) && toLower(menu.name).includes(searchValue)) {
    return matchMultiplier;
  } else if (
    !isEmpty(foodType) &&
    !isEqual(foodType, foodItemType.all.name) &&
    foodTypeFilter.includes(menu.type)
  ) {
    return matchMultiplier;
  } else if (
    !isEmpty(subCategoryMatch) &&
    !isEmpty(searchValue) &&
    toLower(subCategoryMatch).includes(searchValue)
  ) {
    return matchMultiplier;
  } else if (
    !isEmpty(categoryMatch) &&
    !isEmpty(searchValue) &&
    toLower(categoryMatch).includes(searchValue)
  ) {
    return matchMultiplier;
  } else if (
    isEmpty(searchValue) &&
    (isEmpty(foodType) || isEqual(foodType, foodItemType.all.name))
  ) {
    return matchMultiplier;
  }
  return false;
};

export const filterMenuItemAndCategory = (
  section,
  sValue,
  foodType,
  disabledCategories,
  filterItems,
  excludedItems,
  maxItemPrice,
) => {
  let found = false;
  let updatedItem = section;
  if (Array.isArray(section.menuItems) && !isEmpty(section.menuItems)) {
    const filteredMenuItems = updatedItem.menuItems.filter((menuItem) =>
      filterMenuItems({
        searchValue: sValue,
        foodType,
        menu: menuItem,
        categoryMatch: section.name,
        filterItems,
        categoryId: section.ownerParentId,
        excludedItems,
        maxItemPrice,
      }),
    );
    if (filteredMenuItems.length > 0) {
      found = true;
    }
    updatedItem.menuItems = filteredMenuItems;
  } else if (Array.isArray(section.categories) && !isEmpty(section.categories)) {
    const filteredCategories = updatedItem.categories.filter((category) => {
      let updatedCategory = category;

      if (Array.isArray(category.menuItems)) {
        const filteredMenuItems = category.menuItems.filter((menuItem) =>
          filterMenuItems({
            searchValue: sValue,
            foodType,
            menu: menuItem,
            categoryMatch: section.name,
            subCategoryMatch: category.name,
            filterItems,
            categoryId: section.ownerParentId,
            subCategoryId: category.ownerParentId,
            excludedItems,
            maxItemPrice,
          }),
        );
        updatedCategory.menuItems = filteredMenuItems;
        if (filteredMenuItems.length > 0) {
          if (
            disabledCategories &&
            isMenuCategoryAvailable(updatedCategory, null, disabledCategories)
          ) {
            return updatedCategory;
          } else if (!disabledCategories) {
            return updatedCategory;
          }
        }
      }
    });
    if (filteredCategories.length > 0) {
      found = true;
    }
    updatedItem.categories = filteredCategories;
  }
  return found ? updatedItem : false;
};

export const filterMenu = ({
  searchValue,
  foodType,
  menu,
  disabledCategories,
  filterItems,
  excludedItems,
  maxItemPrice,
}) => {
  let value = searchValue;
  let updatedMenu = cloneDeep(menu);
  let sValue = toLower(value.trim()); // modified the user search input to the generic form
  let result =
    (!isEmpty(sValue) ||
      (!isEmpty(foodType) && foodType !== 'ALL') ||
      !isEmpty(filterItems) ||
      !!maxItemPrice) &&
    Array.isArray(updatedMenu)
      ? updatedMenu.filter((item) => {
          let filteredSection = filterMenuItemAndCategory(
            item,
            sValue,
            foodType,
            null,
            filterItems,
            excludedItems,
            maxItemPrice,
          );
          if (
            !isEmpty(get(filteredSection, 'menuItems', [])) ||
            !isEmpty(get(filteredSection, 'categories', []))
          ) {
            return filteredSection;
          }
        })
      : menu;
  return isMenuApplicable(result, disabledCategories);
};

export const isMenuApplicable = (sections, disabledCategories, setFoodTypes) => {
  let draftFilters = [];
  let draftSections = sections.reduce((acc, section) => {
    let filteredSection = cloneDeep(section);
    if (
      get(section, 'id') &&
      section.isEnabled &&
      checkAvailableInSchedule(get(section, 'schedules')) &&
      isMenuCategoryAvailable(section, null, disabledCategories) &&
      (!isEmpty(get(section, 'menuItems', [])) || !isEmpty(get(section, 'categories', [])))
    ) {
      if (
        !isEmpty(get(section, 'menuItems', [])) &&
        isMenuCategoryAvailable(section, null, disabledCategories)
      ) {
        const filteredMenuItems = section.menuItems.filter((menuItem) => {
          if (menuItem.isEnabled && checkAvailableInSchedule(get(menuItem, 'schedules'))) {
            return menuItem;
          }
        });
        if (filteredMenuItems.length > 0) {
          if (filteredMenuItems.some((item) => !draftFilters.includes(item.type))) {
            let filtersToAdd = filteredMenuItems
              .filter((item) => !draftFilters.includes(item.type))
              .map((item) => item.type);
            draftFilters.push(...filtersToAdd);
          }
          filteredSection.menuItems = filteredMenuItems;
        } else {
          filteredSection.menuItems = [];
        }
      } else if (!isEmpty(get(section, 'categories', []))) {
        let filteredCategories = get(section, 'categories', []);
        filteredCategories = filteredCategories
          .map((category) => {
            let updatedCategory = cloneDeep(category);
            if (
              updatedCategory.isEnabled &&
              checkAvailableInSchedule(get(updatedCategory, 'schedules')) &&
              isMenuCategoryAvailable(updatedCategory, null, disabledCategories) &&
              !isEmpty(get(updatedCategory, 'menuItems', []))
            ) {
              const filteredMenuItems = updatedCategory.menuItems.filter((menuItem) => {
                if (menuItem.isEnabled && checkAvailableInSchedule(get(menuItem, 'schedules'))) {
                  return menuItem;
                }
              });

              if (filteredMenuItems.length > 0) {
                if (filteredMenuItems.some((item) => !draftFilters.includes(item.type))) {
                  let filtersToAdd = filteredMenuItems
                    .filter((item) => !draftFilters.includes(item.type))
                    .map((item) => item.type);
                  draftFilters.push(...filtersToAdd);
                }
                updatedCategory.menuItems = filteredMenuItems;
                return updatedCategory;
              }
            }
          })
          .filter((item) => item !== undefined);
        if (filteredCategories.length > 0) {
          filteredSection.categories = filteredCategories;
        } else {
          filteredSection.categories = [];
        }
      }
    } else if (isEqual(get(section, 'name', ''), recommendedCategory.name)) {
      let filteredMenuItems = get(section, 'menuItems', []).filter((item) =>
        !isEmpty(get(item, 'schedules'))
          ? !!item.isEnabled && checkAvailableInSchedule(get(item, 'schedules'))
          : !!item.isEnabled &&
            isCategoryInAvailableSchedule(
              get(item, 'menuCategoryDto', {}),
              get(item, 'menuCategoryDto.parentCategory', {}),
            ),
      );
      if (!isEmpty(filteredMenuItems)) {
        filteredSection.menuItems = filteredMenuItems;
      } else {
        filteredSection.menuItems = [];
      }
    } else {
      filteredSection.menuItems = [];
      filteredSection.categories = [];
    }
    if (
      !isEmpty(get(filteredSection, 'menuItems', [])) ||
      !isEmpty(get(filteredSection, 'categories'))
    ) {
      acc.push(filteredSection);
    }
    return acc;
  }, []);

  if (setFoodTypes) {
    setFoodTypes(draftFilters);
  }
  return draftSections;
};

export const isMenuCategoryAvailable = (
  menuCategory = {},
  parentMenuCategory = {},
  disabledCategories,
) => {
  const isSelfScopedMenuCategoryAvailable = (category) =>
    get(category, 'isEnabled', false) && !disabledCategories.includes(get(category, 'id'));

  const hasParentMenuCategory = !isEmpty(parentMenuCategory);
  if (!hasParentMenuCategory) return isSelfScopedMenuCategoryAvailable(menuCategory); // does not have parent category

  // has parent category, so check for its availability
  return (
    isSelfScopedMenuCategoryAvailable(menuCategory) &&
    isSelfScopedMenuCategoryAvailable(parentMenuCategory)
  );
};

export const isCategoryInAvailableSchedule = (menuCategory = {}, parentMenuCategory = {}) => {
  const isSelfScopedMenuCategoryAvailable = (category) =>
    get(category, 'isEnabled', false) && checkAvailableInSchedule(get(category, 'schedules'));
  const hasParentMenuCategory = !isEmpty(parentMenuCategory);
  const hasMenuCategory = !isEmpty(menuCategory);
  if (!hasParentMenuCategory) return isSelfScopedMenuCategoryAvailable(menuCategory);
  else {
    if (hasMenuCategory && !isEmpty(get(menuCategory, 'schedules'))) {
      return isSelfScopedMenuCategoryAvailable(menuCategory);
    }
    return isSelfScopedMenuCategoryAvailable(parentMenuCategory);
  }
};

export const renderCartItemsCount = (items) => {
  let count = items.length;
  items.forEach((item) => {
    if (item.quantity > 1) {
      count = count + item.quantity - 1;
    }
  });
  return count;
};

export const renderCartItemsPrice = (items, offeredItems, offerCoupon) => {
  const discountValue = get(offerCoupon, 'value', 0);
  const discountType = get(offerCoupon, 'pointUnit')
    ? get(offerCoupon, 'pointUnit')
    : get(offerCoupon, 'unit');
  const calculateItemPrice = (item, isItemOffered) => {
    if (!isEmpty(item.selectedVariation)) {
      if (isItemOffered) {
        if (isEqual(discountType, pointUnits.percent)) {
          price =
            price +
            get(item, 'selectedVariation.price', 0) * item.quantity -
            (get(item, 'selectedVariation.price', 0) * item.quantity * discountValue) / 100;
        } else {
          price =
            price +
            get(item, 'selectedVariation.price', 0) * item.quantity -
            item.quantity * discountValue;
        }
      } else {
        price = price + get(item, 'selectedVariation.price', 0) * item.quantity;
      }
    } else {
      let priceUnit = item.unitPrice ? item.unitPrice : item.price;
      if (isItemOffered) {
        if (isEqual(discountType, pointUnits.percent)) {
          price =
            price + priceUnit * item.quantity - (priceUnit * item.quantity * discountValue) / 100;
        } else {
          price = price + priceUnit * item.quantity - item.quantity * discountValue;
        }
      } else {
        price = price + priceUnit * item.quantity;
      }
    }

    if (!isEmpty(item.orderCustomizations)) {
      item.orderCustomizations.forEach((element) => {
        price = price + element.price * item.quantity;
      });
    }
  };
  let price = 0;
  items.forEach((item) => {
    if (item.isOfferedItem) {
      calculateItemPrice(item, true);
    } else {
      calculateItemPrice(item);
    }
  });

  let isItemOffered = true;
  offeredItems.forEach((item) => {
    calculateItemPrice(item, isItemOffered);
  });

  return roundOff(price, priceprecision);
};

export const checkAvailableInSchedule = (schedules = []) =>
  isEmpty(schedules) ||
  schedules.some((schedule) =>
    checkTimeInRange(get(schedule, 'timeFrom'), get(schedule, 'timeTo')),
  );

export const renderFoodIcon = (type) => {
  switch (type) {
    case foodItemType.veg.name:
      return <Icons type={foodItemType.veg.name} />;
    case foodItemType.nonVeg.name:
      return <Icons type={foodItemType.nonVeg.name} />;
    case foodItemType.egg.name:
      return <Icons type={foodItemType.nonVeg.name} />;

    default:
      break;
  }
};

export const addItemInCart = ({ item, cartItems, cartOfferedItems = [], setCartItems }) => {
  const buyItems = cartItems ? cartItems.filter((item) => !isEmpty(item.offerId)) : [];
  const offerExclusiveCartItems = cartItems
    ? cartItems.filter((item) => isEmpty(item.offerId))
    : [];
  const allCartItems = [...offerExclusiveCartItems, ...buyItems, ...cartOfferedItems];
  const cartItemObj = allCartItems.find((cartItem) =>
    item.hasVariations
      ? isEqual(item.id, cartItem.menuItemId) &&
        isEqual(item.selectedVariation.id, cartItem.variationId) &&
        isUndefined(get(item, 'newCustomizations')) &&
        !cartItem.isOfferedItem &&
        !item.isOfferedItem
      : isEqual(item.id, cartItem.menuItemId) &&
        isUndefined(get(item, 'newCustomizations')) &&
        !cartItem.isOfferedItem &&
        !item.isOfferedItem,
  );
  let draftItems = [];
  if (!isEmpty(cartItemObj)) {
    let newArr = cartItems;
    let itemIndex = -1;
    if (item.hasVariations) {
      itemIndex = newArr
        .map(
          (d) =>
            isEmpty(get(d, 'offerId')) &&
            isEqual(d.variationId, item.selectedVariation.id) &&
            d.menuItemId,
        )
        .lastIndexOf(item.id);
    } else {
      itemIndex = newArr
        .map((d) => isEmpty(get(d, 'offerId')) && d.menuItemId)
        .lastIndexOf(item.id);
    }
    if (itemIndex > -1) {
      newArr[itemIndex] = {
        ...newArr[itemIndex],
        quantity: newArr[itemIndex].quantity + get(item, 'quantity', 1),
      };
      draftItems = [...newArr];
      setCartItems && setCartItems(draftItems);
    } else if (!isEmpty(get(cartItemObj, 'offerId'))) {
      let itemWithOffer = {};
      let itemIndex;
      let newItem = {};
      if (get(cartItemObj, 'isOfferedItem', false)) {
        itemIndex = cartOfferedItems.map((d) => d.menuItemId).lastIndexOf(item.id);
        itemWithOffer = cartOfferedItems[itemIndex];
      } else {
        itemIndex = buyItems.map((d) => d.menuItemId).lastIndexOf(item.id);
        itemWithOffer = buyItems[itemIndex];
      }

      newItem['menuItemId'] = get(item, 'id');
      newItem['orderCustomizations'] = get(itemWithOffer, 'orderCustomizations', []);
      newItem['quantity'] = get(item, 'quantity', 1);
      newItem['price'] = get(item, 'price');
      newItem['variationId'] = get(itemWithOffer, 'selectedVariation.id')
        ? get(itemWithOffer, 'selectedVariation.id')
        : get(itemWithOffer, 'variationId');
      newItem['selectedVariation'] = get(itemWithOffer, 'selectedVariation');
      draftItems = [...cartItems, newItem];
      setCartItems && setCartItems(draftItems);
    }
  } else {
    let newItem = {};
    if (!isEmpty(get(item, 'newCustomizations', []))) {
      newItem['menuItemId'] = get(item, 'id');
      newItem['orderCustomizations'] = get(item, 'newCustomizations', []);
      newItem['quantity'] = get(item, 'quantity', 1);
      newItem['price'] = get(item, 'price');
      newItem['variationId'] = get(item, 'selectedVariation.id', null);
      newItem['selectedVariation'] = get(item, 'selectedVariation');
    } else if (!isEmpty(get(item, 'customizations', []))) {
      newItem['menuItemId'] = get(item, 'id');
      newItem['orderCustomizations'] = get(item, 'customizations', []);
      newItem['quantity'] = get(item, 'quantity', 1);
      newItem['price'] = get(item, 'price');
      newItem['variationId'] = get(item, 'selectedVariation.id', null);
      newItem['selectedVariation'] = get(item, 'selectedVariation');
    } else {
      // case where variations doesn't contains customizations
      newItem['menuItemId'] = get(item, 'id');
      newItem['orderCustomizations'] = [];
      newItem['quantity'] = get(item, 'quantity', 1);
      newItem['price'] = get(item, 'price');
      newItem['variationId'] = get(item, 'selectedVariation.id', null);
      newItem['selectedVariation'] = get(item, 'selectedVariation');
    }
    newItem['offerId'] = get(item, 'offerId');
    newItem['isOfferedItem'] = get(item, 'isOfferedItem');
    draftItems = [...cartItems, newItem];
    setCartItems && setCartItems(draftItems);
  }
  return draftItems;
};

export const removeItemFromCart = ({
  item,
  cartItems,
  cartOfferedItems,
  itemType,
  onRemoveBogoItemsClick,
  setShowOfferRemoveWarningPopup,
  setShowMultipleCustomizationPopup,
  setCartItems,
  saveCart,
}) => {
  const { isCustomizable } = item;
  const extraItems = cartItems.filter((item) => isEmpty(get(item, 'offerId')));
  if (isEqual(itemType, 'BUY')) {
    onRemoveBogoItemsClick();
    return;
  }
  const itemInExtra = extraItems.some((cartItem) => isEqual(item.id, cartItem.menuItemId));
  let draftItems = [];
  if (!itemInExtra) {
    setShowOfferRemoveWarningPopup && setShowOfferRemoveWarningPopup(true);
  } else if (
    cartItems.some(
      (cartItem) => isEqual(item.id, cartItem.menuItemId) && isEmpty(get(cartItem, 'offerId')),
    )
  ) {
    let newArr = cartItems;
    const itemIndex = newArr.findIndex(
      (cartItem) => isEqual(item.id, cartItem.menuItemId) && isEmpty(get(cartItem, 'offerId')),
    );
    const customizationItems = newArr.filter(
      (cartItem) => isEqual(item.id, cartItem.menuItemId) && isEmpty(get(cartItem, 'offerId')),
    );
    if (isCustomizable && customizationItems && customizationItems.length > 1) {
      setShowMultipleCustomizationPopup && setShowMultipleCustomizationPopup(true);
    } else {
      if (newArr[itemIndex].quantity === 1) {
        newArr.splice(itemIndex, 1);
        draftItems = [...newArr];
        setCartItems && setCartItems(draftItems);
        if (isEqual(cartItems.length, 0)) {
          saveCart && saveCart({ items: cartItems, onlyUpdate: true, withCustomization: false });
        }
      } else {
        newArr[itemIndex] = { ...newArr[itemIndex], quantity: newArr[itemIndex].quantity - 1 };
        draftItems = [...newArr];
        setCartItems && setCartItems([...newArr]);
      }
    }
  }
  return draftItems;
};
export const getAllMenuItems = (menuData) => {
  let allMenuItems = [];

  const extractMenuItems = (items) => {
    items.forEach((item) => {
      if (item.menuItems && item.menuItems.length > 0) {
        allMenuItems = allMenuItems.concat(item.menuItems);
      }
      if (item.categories && item.categories.length > 0) {
        extractMenuItems(item.categories);
      }
    });
  };

  extractMenuItems(menuData);
  return allMenuItems;
};
